import { Component } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';

class SmartErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorType: null,
      retryCount: 0,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Categorize the error
    const errorType = this.categorizeError(error);
    
    // Send to Sentry with categorization
    Sentry.withScope((scope) => {
      scope.setExtras({ ...errorInfo, errorType });
      scope.setLevel('error');
      scope.setTag('error_type', errorType);
      Sentry.captureException(error);
    });

    this.setState({
      error,
      errorInfo,
      errorType
    });

    // Show smart toast based on error type
    this.showSmartToast(errorType, error);
  }

  categorizeError = (error) => {
    const errorMessage = error?.message?.toLowerCase() || '';
    const errorStack = error?.stack?.toLowerCase() || '';
    
    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return 'network';
    }
    if (errorMessage.includes('auth') || errorMessage.includes('unauthorized')) {
      return 'auth';
    }
    if (errorMessage.includes('chunk') || errorStack.includes('dynamicimport')) {
      return 'chunk-load';
    }
    if (errorMessage.includes('supabase') || errorMessage.includes('api')) {
      return 'api';
    }
    return 'unknown';
  };

  showSmartToast = (errorType, error) => {
    const actions = this.getRecoveryActions(errorType);
    
    toast.error(this.getErrorTitle(errorType), {
      description: this.getErrorDescription(errorType),
      duration: Infinity,
      action: actions[0], // Primary action
      secondaryAction: actions[1] // Secondary action if available
    });
  };

  getErrorTitle = (errorType) => {
    switch (errorType) {
      case 'network':
        return 'Connection lost';
      case 'auth':
        return 'Authentication error';
      case 'chunk-load':
        return 'Loading error';
      case 'api':
        return 'Server error';
      default:
        return 'Something went wrong';
    }
  };

  getErrorDescription = (errorType) => {
    switch (errorType) {
      case 'network':
        return 'Check your internet connection and try again';
      case 'auth':
        return 'Your session may have expired';
      case 'chunk-load':
        return 'Failed to load application resources';
      case 'api':
        return 'Unable to communicate with server';
      default:
        return 'We\'ve been notified and are looking into it';
    }
  };

  getRecoveryActions = (errorType) => {
    const actions = [];
    
    switch (errorType) {
      case 'network':
        actions.push({
          label: 'Retry',
          onClick: () => this.handleRetry()
        });
        break;
      
      case 'auth':
        actions.push({
          label: 'Re-login',
          onClick: () => this.handleRelogin()
        });
        actions.push({
          label: 'Go home',
          onClick: () => this.handleGoHome()
        });
        break;
      
      case 'chunk-load':
        actions.push({
          label: 'Reload app',
          onClick: () => this.handleReload()
        });
        break;
      
      case 'api':
        actions.push({
          label: 'Try again',
          onClick: () => this.handleRetry()
        });
        actions.push({
          label: 'Copy error',
          onClick: () => this.handleCopyError()
        });
        break;
      
      default:
        actions.push({
          label: 'Reset',
          onClick: () => this.handleReset()
        });
        actions.push({
          label: 'Reload',
          onClick: () => this.handleReload()
        });
    }
    
    return actions;
  };

  // Recovery action handlers
  handleRetry = () => {
    if (this.state.retryCount < 3) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }));
      toast.success('Retrying...');
    } else {
      toast.error('Max retries reached. Please reload the page.');
    }
  };

  handleRelogin = () => {
    // Clear session in whichever storage is in use (localStorage or Electron file)
    supabase.auth.signOut().finally(() => {
      window.location.href = '/login';
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReload = () => {
    // Save current state to localStorage first
    const currentState = {
      selectedConversation: localStorage.getItem('selectedConversation'),
      draftMessage: localStorage.getItem('draftMessage')
    };
    localStorage.setItem('errorRecoveryState', JSON.stringify(currentState));
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorType: null,
      retryCount: 0
    });
  };

  handleCopyError = () => {
    const errorDetails = `Error: ${this.state.error?.message}\nType: ${this.state.errorType}\nStack: ${this.state.error?.stack}`;
    navigator.clipboard.writeText(errorDetails);
    toast.success('Error details copied to clipboard');
  };

  render() {
    if (this.state.hasError) {
      const { errorType } = this.state;
      
      return (
        <div className="min-h-screen-mobile bg-page flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-bg-panel rounded-lg border border-border-default p-8 text-center">
            <h2 className="text-xl font-semibold text-primary mb-2">
              {this.getErrorTitle(errorType)}
            </h2>
            
            <p className="text-secondary mb-6">
              {this.getErrorDescription(errorType)}
            </p>
            
            <div className="flex flex-col gap-3">
              {this.renderRecoveryButtons(errorType)}
            </div>
            
            {/* Error details toggle */}
            <button
              onClick={() => this.setState({ showDetails: !this.state.showDetails })}
              className="mt-6 text-secondary hover:text-primary text-sm flex items-center gap-1 mx-auto"
            >
              <svg className={`w-4 h-4 transition-transform ${this.state.showDetails ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Developer info
            </button>
            
            {/* Error details */}
            {this.state.showDetails && (
              <div className="mt-4 p-4 bg-bg-well rounded-lg text-left">
                <p className="text-xs text-secondary mb-2">Error Type: {this.state.errorType}</p>
                <pre className="text-xs text-secondary font-mono overflow-auto max-h-40 whitespace-pre-wrap">
                  {this.state.error?.stack || this.state.error?.toString()}
                </pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }

  renderRecoveryButtons = (errorType) => {
    const buttons = [];
    
    switch (errorType) {
      case 'network':
        buttons.push(
          <button
            key="retry"
            onClick={this.handleRetry}
            className="w-full bg-bg-card text-primary py-2 px-4 rounded-lg hover:bg-bg-hover transition-colors flex items-center justify-center gap-2"
          >
            Retry Connection
          </button>
        );
        break;
      
      case 'auth':
        buttons.push(
          <button
            key="relogin"
            onClick={this.handleRelogin}
            className="w-full bg-bg-card text-primary py-2 px-4 rounded-lg hover:bg-bg-hover transition-colors flex items-center justify-center gap-2"
          >
            Re-login
          </button>,
          <button
            key="home"
            onClick={this.handleGoHome}
            className="w-full bg-bg-card text-primary py-2 px-4 rounded-lg hover:bg-bg-hover transition-colors flex items-center justify-center gap-2"
          >
            Go to Home
          </button>
        );
        break;
      
      case 'chunk-load':
        buttons.push(
          <button
            key="reload"
            onClick={this.handleReload}
            className="w-full bg-bg-card text-primary py-2 px-4 rounded-lg hover:bg-bg-hover transition-colors flex items-center justify-center gap-2"
          >
            Reload Application
          </button>
        );
        break;
      
      default:
        buttons.push(
          <button
            key="reset"
            onClick={this.handleReset}
            className="w-full bg-bg-card text-primary py-2 px-4 rounded-lg hover:bg-bg-hover transition-colors"
          >
            Try Again
          </button>,
          <button
            key="reload"
            onClick={this.handleReload}
            className="w-full bg-bg-card text-primary py-2 px-4 rounded-lg hover:bg-bg-hover transition-colors"
          >
            Reload Page
          </button>,
          <button
            key="copy"
            onClick={this.handleCopyError}
            className="w-full bg-transparent text-secondary py-2 px-4 rounded-lg hover:text-primary transition-colors flex items-center justify-center gap-2"
          >
            Copy Error Details
          </button>
        );
    }
    
    return buttons;
  };
}

SmartErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SmartErrorBoundary;
